/**
 * Heroes — the character you pick and carry through a campaign.
 *
 * A hero is three things at once, and the design leans on all three:
 *   • a *unit* on the tactical field, with a champion's silhouette and a
 *     champion's refusal to rout,
 *   • a *general* on the world map, whose passives apply to the whole army he
 *     leads, so who you send matters as much as what you send,
 *   • a *character sheet* that survives between battles, so a long campaign has
 *     something that grows other than the size of your treasury.
 *
 * Levels come from winning battles and taking cities. Skill points are spent on
 * a small tree; passives are always on; actives are cast in battle like a
 * Sovereign Power but tied to the hero's position on the field.
 */

import { DamageType, UF } from '../sim/types.ts';

export type HeroArchetype = 'warlord' | 'castellan' | 'huntmaster' | 'sorcerer' | 'prelate';

export interface HeroPassive {
  id: string;
  name: string;
  /** Exact mechanical text. */
  rules: string;
  /** Applied to `world.mods` when this hero leads the army. */
  mods: Partial<Record<string, number>>;
}

export interface HeroSkill {
  id: string;
  name: string;
  flavour: string;
  rules: string;
  /** Level at which it can first be bought. */
  minLevel: number;
  /** Skill points to buy. */
  cost: number;
  /** Prerequisite skill ids. */
  requires: string[];
  /** If set, buying this grants a Sovereign Power usable in battle. */
  grantsPower?: string;
  /** If set, buying this improves a passive by the given amounts. */
  mods?: Partial<Record<string, number>>;
  /** Layout in the skill tree UI. */
  row: number;
  col: number;
}

export interface HeroClass {
  id: HeroArchetype;
  name: string;
  epithet: string;
  flavour: string;
  /** The unit type this hero fights as. */
  unitId: string;
  /** Starting stat multipliers on that unit. */
  hp: number;
  damage: number;
  speed: number;
  /** Which factions may field this class. Empty = all. */
  factions: string[];
  startingPassive: HeroPassive;
  skills: HeroSkill[];
}

/** XP needed for each level, cumulative. Level 1 is the start. */
export const HERO_LEVEL_XP = [
  0, 120, 300, 560, 900, 1340, 1900, 2600, 3460, 4500, 5760, 7260, 9020, 11060, 13400,
];

export const HERO_MAX_LEVEL = HERO_LEVEL_XP.length;

export function levelForXp(xp: number): number {
  let lvl = 1;
  for (let i = 0; i < HERO_LEVEL_XP.length; i++) if (xp >= HERO_LEVEL_XP[i]) lvl = i + 1;
  return Math.min(HERO_MAX_LEVEL, lvl);
}

export function xpForNextLevel(level: number): number {
  return HERO_LEVEL_XP[Math.min(HERO_LEVEL_XP.length - 1, level)] ?? Infinity;
}

/** One skill point per level, plus one at every third. */
export function skillPointsAt(level: number): number {
  return level - 1 + Math.floor((level - 1) / 3);
}

function skill(
  id: string,
  name: string,
  flavour: string,
  rules: string,
  minLevel: number,
  row: number,
  col: number,
  extra: Partial<HeroSkill> = {},
): HeroSkill {
  return { id, name, flavour, rules, minLevel, cost: 1, requires: [], row, col, ...extra };
}

export const HERO_CLASSES: HeroClass[] = [
  {
    id: 'warlord',
    name: 'Warlord',
    epithet: 'leads from the front, which is the only place he can see from',
    flavour:
      'Promoted by acclamation, in the field, by men who had run out of officers. Has never been told he cannot go back.',
    unitId: 'hero_warlord',
    hp: 1,
    damage: 1,
    speed: 1,
    factions: [],
    startingPassive: {
      id: 'presence',
      name: 'Presence',
      rules: 'Companies within 8 tiles of the Warlord will not rout above a third strength.',
      mods: { moraleFloor: 0.3 },
    },
    skills: [
      skill('wl_bulwark', 'Bulwark', 'Stands where he is put.', 'Army units +12% health.', 2, 0, 0, {
        mods: { unitHp: 1.12 },
      }),
      skill('wl_edge', 'The Edge', 'Sharpened before dawn, every dawn.', 'Army units +12% damage.', 2, 0, 2, {
        mods: { unitDamage: 1.12 },
      }),
      skill('wl_charge', 'Thunder Run', 'The ground remembers it.', 'Cavalry charge damage +40%.', 4, 1, 1, {
        requires: ['wl_bulwark'],
        mods: { cavalryCharge: 1.4 },
      }),
      skill('wl_rally', 'Rally the Banner', 'The rag on the stick, and what it does.', 'Grants Rally the Banner.', 4, 1, 3, {
        requires: ['wl_edge'],
        grantsPower: 'rally_banner',
      }),
      skill('wl_veteran', 'Hard School', 'Everyone who lives learns something.', 'Veterancy accrues 80% faster.', 6, 2, 0, {
        requires: ['wl_bulwark'],
        mods: { veterancyRate: 1.8 },
      }),
      skill('wl_tremor', 'Tremor', 'He hits the ground and the ground answers.', 'Grants Tremor.', 7, 2, 2, {
        requires: ['wl_charge'],
        grantsPower: 'tremor',
      }),
      skill('wl_last', 'Last Stand', 'Not a plan. A habit.', 'Grants Last Stand.', 10, 3, 1, {
        cost: 2,
        requires: ['wl_veteran', 'wl_tremor'],
        grantsPower: 'last_stand',
      }),
    ],
  },
  {
    id: 'castellan',
    name: 'Castellan',
    epithet: 'keeps the walls, and the ledger, and the peace',
    flavour:
      'Has never lost a siege, which she attributes to arithmetic and everyone else attributes to spite.',
    unitId: 'hero_castellan',
    hp: 1.15,
    damage: 0.85,
    speed: 0.9,
    factions: [],
    startingPassive: {
      id: 'stonewright',
      name: 'Stonewright',
      rules: 'Walls +40% health, and buildings finish 25% faster.',
      mods: { wallHp: 1.4, buildSpeed: 1.25 },
    },
    skills: [
      skill('ca_masons', 'Masons’ Guild', 'They have opinions and a monopoly.', 'Buildings cost 20% less.', 2, 0, 0, {
        mods: { buildCost: 0.8 },
      }),
      skill('ca_towers', 'Enfilade', 'Angles that make an approach embarrassing.', 'Towers +35% damage.', 2, 0, 2, {
        mods: { towerDamage: 1.35 },
      }),
      skill('ca_ranging', 'Ranging Glass', 'Two lenses and a great deal of argument.', 'Towers +30% range.', 4, 1, 3, {
        requires: ['ca_towers'],
        mods: { towerRange: 1.3 },
      }),
      skill('ca_siegeproof', 'Siege-Proofing', 'Layers. Between the layers, sand.', 'Siege damage to your structures −40%.', 5, 1, 0, {
        requires: ['ca_masons'],
        mods: { siegeResist: 0.6 },
      }),
      skill('ca_bramble', 'Bramblework', 'Grown in a hurry, and it shows in the thorns.', 'Grants Wall of Bramble.', 6, 2, 1, {
        requires: ['ca_siegeproof'],
        grantsPower: 'wall_of_bramble',
      }),
      skill('ca_shatter', 'Shatterstone', 'A word for taking things apart, said to the parts.', 'Grants Shatterstone.', 8, 2, 3, {
        requires: ['ca_ranging'],
        grantsPower: 'shatterstone',
      }),
      skill('ca_redoubt', 'The Last Redoubt', 'Where the last of it happens, by arrangement.', 'Walls +80% health; the Hearth repairs between waves.', 11, 3, 2, {
        cost: 2,
        requires: ['ca_bramble', 'ca_shatter'],
        mods: { wallHp: 1.8 },
      }),
    ],
  },
  {
    id: 'huntmaster',
    name: 'Huntmaster',
    epithet: 'arrives before the news of him',
    flavour: 'Counts the enemy by smell and is seldom wrong, which everyone finds more unsettling than useful.',
    unitId: 'hero_huntmaster',
    hp: 0.85,
    damage: 1.1,
    speed: 1.3,
    factions: [],
    startingPassive: {
      id: 'outrider',
      name: 'Outrider',
      rules: 'The whole army moves 20% faster; archers gain 1.5 tiles of range.',
      mods: { moveSpeed: 1.2, archerRange: 1.5 },
    },
    skills: [
      skill('hu_fletch', 'Fletchers’ Guild', 'Paid by the hundred, counted by the thousand.', 'Archers +20% damage.', 2, 0, 0, {
        mods: { unitDamage: 1.1 },
      }),
      skill('hu_range', 'Ashglass', 'Ground from a window that survived the burning.', 'Archers +2 tiles range.', 3, 0, 2, {
        mods: { archerRange: 2 },
      }),
      skill('hu_long_sight', 'The Long Sight', 'She saw them at the ford an hour before anybody else did.', 'Grants Hearthlight.', 4, 1, 1, {
        requires: ['hu_fletch'],
        grantsPower: 'hearth_light',
      }),
      skill('hu_swift', 'The Long Run', 'Faster than they should be.', 'Army +20% speed, +15% attack speed.', 5, 1, 3, {
        requires: ['hu_range'],
        mods: { moveSpeed: 1.2, attackSpeed: 1.15 },
      }),
      skill('hu_gale', 'Gale', 'The wind off the Ashen Steps, asked for a favour.', 'Grants Gale.', 7, 2, 0, {
        requires: ['hu_long_sight'],
        grantsPower: 'gale',
      }),
      skill('hu_amber', 'Time-in-Amber', 'A small, local disagreement with the hour.', 'Grants Time-in-Amber.', 9, 2, 3, {
        requires: ['hu_swift'],
        grantsPower: 'time_in_amber',
      }),
      skill('hu_ambush', 'The Quiet Mile', 'You do not hear them. That is the whole of it.', 'Army +25% damage in the first 30 seconds of a battle.', 11, 3, 1, {
        cost: 2,
        requires: ['hu_gale', 'hu_amber'],
        mods: { unitDamage: 1.15 },
      }),
    ],
  },
  {
    id: 'sorcerer',
    name: 'Sorcerer',
    epithet: 'is asked weekly to stop, and is weekly indispensable',
    flavour:
      'Keeps a tower, a ledger of debts owed to things without names, and a very short list of people who may knock.',
    unitId: 'hero_sorcerer',
    hp: 0.75,
    damage: 1.2,
    speed: 0.95,
    factions: [],
    startingPassive: {
      id: 'conduit',
      name: 'Conduit',
      rules: 'Will regenerates 60% faster and maximum Will is +40.',
      mods: { willRegen: 1.6, willMax: 40 },
    },
    skills: [
      skill('so_severity', 'Severity', 'The kind part is optional and has been removed.', 'Powers +30% damage.', 2, 0, 0, {
        mods: { powerDamage: 1.3 },
      }),
      skill('so_amplitude', 'Amplitude', 'Louder is, unfortunately, better.', 'Powers +30% radius.', 2, 0, 2, {
        mods: { powerRadius: 1.3 },
      }),
      skill('so_focus', 'Focus', 'Say less. Mean it harder.', 'Powers cool down 30% faster.', 4, 1, 1, {
        requires: ['so_severity'],
        mods: { powerCooldown: 0.7 },
      }),
      /**
       * Four skills, four different powers, and not one of them is a nuke any
       * more. This tree was Chain Lightning into Starfall into Ember Rain —
       * the whole retired family in one column — and rebuilding it was the
       * larger half of the cut. The Sovereign now spends its levels on the
       * four survivors nothing else grants: strip their armour, buy damage
       * with your own men's blood, put bodies on the field, and break the
       * wave's nerve.
       */
      skill('so_quicklime', 'Quicklime', 'Used, once, on a field. The field has not recovered.', 'Grants Quicklime.', 4, 1, 3, {
        requires: ['so_amplitude'],
        grantsPower: 'quicklime',
      }),
      skill('so_blood', 'The Blood Price', 'The ledger is honest, at least.', 'Grants the Blood Price.', 6, 2, 0, {
        requires: ['so_focus'],
        grantsPower: 'blood_price',
      }),
      skill('so_pale', 'Pale Summons', 'Twelve of them. Always twelve.', 'Grants Pale Summons.', 8, 2, 2, {
        requires: ['so_quicklime'],
        grantsPower: 'pale_summons',
      }),
      skill('so_bell', 'The Buried Bell', 'Nine were cast. This is the one they put in the ground.', 'Grants Toll the Bell.', 11, 3, 1, {
        cost: 2,
        requires: ['so_blood', 'so_pale'],
        grantsPower: 'toll_the_bell',
      }),
    ],
  },
  {
    id: 'prelate',
    name: 'Prelate',
    epithet: 'will hear anything, and forgives almost none of it',
    flavour:
      'Carries the ninth bell’s clapper in a satchel and will not explain how it came to be there.',
    unitId: 'hero_prelate',
    hp: 1.05,
    damage: 0.9,
    speed: 1,
    factions: [],
    startingPassive: {
      id: 'sacrament',
      name: 'Sacrament',
      rules: 'Souls from kills +50%, and the settlement’s morale floor is raised.',
      mods: { soulGain: 1.5, moraleFloor: 0.2 },
    },
    skills: [
      skill('pr_sanctify', 'Sanctification', 'A word said over ground that did not ask for it.', 'Grants Sanctified Ground.', 2, 0, 1, {
        grantsPower: 'sanctified_ground',
      }),
      skill('pr_tithe', 'The Grave Tithe', 'A tax the dead pay gladly.', 'Souls from kills +60%.', 3, 0, 3, {
        mods: { soulGain: 1.6 },
      }),
      skill('pr_resolve', 'Iron Resolve', 'An order given in a voice that does not admit retreat.', 'Grants Iron Resolve.', 5, 1, 0, {
        requires: ['pr_sanctify'],
        grantsPower: 'iron_resolve',
      }),
      skill('pr_grave', 'Grave-Call', 'They fell for you once.', 'Grants Grave-Call.', 6, 1, 2, {
        requires: ['pr_tithe'],
        grantsPower: 'grave_call',
      }),
      skill('pr_communion', 'Communion', 'Shared out thinly, and it still goes round.', 'Army heals 10% of damage dealt.', 8, 2, 1, {
        requires: ['pr_resolve'],
        mods: { lifesteal: 0.1 },
      }),
      skill('pr_bell', 'Toll the Bell', 'Nine bells were cast. This is the one they buried.', 'Grants Toll the Bell.', 9, 2, 3, {
        requires: ['pr_grave'],
        grantsPower: 'toll_the_bell',
      }),
      skill('pr_rise', 'The Unburied Pact', 'Nobody signed it. Everybody keeps it.', 'One in four of your fallen rises again.', 12, 3, 2, {
        cost: 2,
        requires: ['pr_communion', 'pr_bell'],
        mods: { corpseRise: 0.25 },
      }),
    ],
  },
];

const BY_ID = new Map<string, HeroClass>();
for (const h of HERO_CLASSES) BY_ID.set(h.id, h);

export function heroClass(id: string): HeroClass {
  const h = BY_ID.get(id);
  if (!h) throw new Error(`Unknown hero class: ${id}`);
  return h;
}

export function skillById(cls: HeroClass, id: string): HeroSkill | undefined {
  return cls.skills.find((s) => s.id === id);
}

/** Names, for generated heroes and enemy champions. */
export const HERO_FIRST_NAMES = [
  'Aldric', 'Bren', 'Cael', 'Dain', 'Eadric', 'Fenn', 'Gareth', 'Halden', 'Ilsa',
  'Jorun', 'Kestrel', 'Lyra', 'Maerwyn', 'Nial', 'Orin', 'Perrin', 'Quenna',
  'Rowan', 'Sable', 'Torvald', 'Ulric', 'Vesper', 'Wynn', 'Yarrow',
];

export const HERO_EPITHETS = [
  'the Unburied', 'of the Ninth Bell', 'Ashhand', 'the Late', 'Gravewake',
  'of Widow Pass', 'the Patient', 'Coldwater', 'the Twice-Buried', 'Thornwake',
  'of the Long Watch', 'Bellringer', 'the Unasked', 'Sixthborn', 'the Quiet',
];

/**
 * Damage type and flags a hero fights with. Heroes are champions: they do not
 * rout, they are large enough to be seen in a crowd, and they hit hard enough
 * that finding them on the field is worth doing.
 */
export const HERO_UNIT_FLAGS = UF.Champion | UF.Large;
export const HERO_DAMAGE_TYPE: Record<HeroArchetype, number> = {
  warlord: DamageType.Slash,
  castellan: DamageType.Blunt,
  huntmaster: DamageType.Pierce,
  sorcerer: DamageType.Arcane,
  prelate: DamageType.Fire,
};
